import styled from 'styled-components';

import { gql } from '@apollo/client';
import { NextPage } from 'next';
import Router from 'next/router';
import { UseCasesAnimation } from '../components/animations/use-cases';
import { CodeSnippets } from '../components/code-snippets';
import { Footer, FOOTER_QUERY } from '../components/footer';
import { Header, HEADER_QUERY } from '../components/header';
import { Col, ResponsiveContainer, Row } from '../components/responsive';
import { Angle } from '../components/ui/angle';
import { Button } from '../components/ui/button';
import { OptimizedImage } from '../components/ui/optimizedImage';
import { Separator } from '../components/ui/separator';
import { ContentStackClient } from '../lib/contentstack';
import { Developers } from '../types/contentstack';
import { IPageProps } from '../types/page';
import { mediaScreen } from '../utils/mediaScreen';

export const Page: NextPage<IPageProps<Developers>> = ({
  header,
  page,
  footer,
}) => {
  return (
    <>
      <Header
        pageTitle={page?.title}
        pageDescription={page?.description}
        {...header}
      />

      <WidthDelimiter>
        <WidthDelimiter style={{ zIndex: 3 }}>
          <Hero>
            <ResponsiveContainer>
              <Row>
                <Col md={8}>
                  <HeroTitle
                    dangerouslySetInnerHTML={{
                      __html: page?.hero?.title ?? '',
                    }}
                  />

                  <HeroText>{page?.hero?.text}</HeroText>

                  <Button
                    mColor="teal"
                    onClick={() => Router.push(page?.hero?.button?.href ?? '')}
                  >
                    {page?.hero?.button?.title}
                  </Button>
                </Col>
              </Row>
            </ResponsiveContainer>
          </Hero>
        </WidthDelimiter>

        <WhiteBackgroundSection>
          <Angle position="top-right" />

          <div className="container">
            <ResponsiveContainer>
              <Row className="header">
                <Col md={6}>
                  <label>{page?.uses?.label}</label>

                  <SectionTitle>{page?.uses?.title}</SectionTitle>
                </Col>
              </Row>

              <Row className="desktop-only">
                <Col xs={12}>
                  <UseCasesAnimation />
                </Col>
              </Row>

              {[1].map(() => {
                const [case1, case2, case3, case4] = page?.uses?.cases ?? [];

                return (
                  <div className="mobile-only" key={1}>
                    <div className="item">
                      <SectionSubTitle>{case1?.title}</SectionSubTitle>
                      <SectionSubText>{case1?.text}</SectionSubText>

                      <OptimizedImage
                        src="/img/developer-case-1.png"
                        width="311"
                        height="230"
                        layout="responsive"
                        alt="connect"
                      />
                    </div>

                    <div className="item">
                      <SectionSubTitle>{case2?.title}</SectionSubTitle>
                      <SectionSubText>{case2?.text}</SectionSubText>

                      <OptimizedImage
                        src="/img/developer-case-2.png"
                        width="311"
                        height="394"
                        layout="responsive"
                        alt="discover"
                      />
                    </div>

                    <div className="item">
                      <SectionSubTitle>{case3?.title}</SectionSubTitle>
                      <SectionSubText>{case3?.text}</SectionSubText>

                      <OptimizedImage
                        src="/img/developer-case-3.png"
                        width="311"
                        height="222"
                        layout="responsive"
                        alt="build"
                      />
                    </div>

                    <div className="item">
                      <SectionSubTitle>{case4?.title}</SectionSubTitle>
                      <SectionSubText>{case4?.text}</SectionSubText>

                      <OptimizedImage
                        src="/img/developer-case-4.png"
                        width="311"
                        height="270"
                        layout="responsive"
                        alt="deploy"
                      />
                    </div>
                  </div>
                );
              })}
            </ResponsiveContainer>
          </div>

          <Angle position="bottom-left" />
        </WhiteBackgroundSection>

        <BlackBackgroundSection>
          <div className="item item0">
            <ResponsiveContainer className="container">
              <Separator />

              <Row>
                <Col md={6}>
                  <SectionTitle>{page?.api?.title}</SectionTitle>
                </Col>

                <Col md={1} />

                <Col md={5}>
                  <SectionText>{page?.api?.text}</SectionText>
                </Col>
              </Row>

              <CodeSnippets />
            </ResponsiveContainer>
          </div>

          {[1].map(() => {
            const [feature1, feature2, feature3, feature4, feature5] =
              page?.features ?? [];

            return (
              <>
                <div className="item item1" key={1}>
                  <ResponsiveContainer className="container">
                    <Row>
                      <Col md={5}>
                        <Separator size="tiny" />

                        <SectionSubTitle>{feature1?.title}</SectionSubTitle>
                        <SectionSubText>{feature1?.text}</SectionSubText>
                      </Col>
                    </Row>
                  </ResponsiveContainer>

                  <div style={{ flex: 1 }} />

                  <img
                    src="/img/data-pipeline.svg"
                    width="500"
                    height="351"
                    className="image"
                    alt="data pipeline"
                  />
                </div>

                <div className="item item2">
                  <ResponsiveContainer>
                    <Row>
                      <Col md={6}>
                        <img
                          src="/img/hard-to-reach-data.svg"
                          width="296"
                          height="166"
                          className="image"
                          alt="hard to reach data"
                          style={{ width: '100%' }}
                        />
                      </Col>

                      <Col md={1} />

                      <Col md={5}>
                        <Separator size="tiny" />

                        <SectionSubTitle>{feature2?.title}</SectionSubTitle>
                        <SectionSubText>{feature2?.text}</SectionSubText>
                      </Col>
                    </Row>
                  </ResponsiveContainer>
                </div>

                <div className="item item3">
                  <ResponsiveContainer className="container">
                    <Row>
                      <Col md={5}>
                        <Separator size="tiny" />

                        <SectionSubTitle>{feature3?.title}</SectionSubTitle>
                        <SectionSubText>{feature3?.text}</SectionSubText>
                      </Col>
                    </Row>
                  </ResponsiveContainer>

                  <div style={{ flex: 1 }} />

                  <img
                    src="/img/cause-and-prediction.svg"
                    width="860"
                    height="465"
                    className="image"
                    alt="cause and prediction"
                  />
                </div>

                <div className="item item4">
                  <img
                    src="/img/buildings.svg"
                    width="336"
                    height="339"
                    className="image"
                    alt="buildings"
                  />

                  <div style={{ flex: 1 }} />

                  <ResponsiveContainer className="container">
                    <Row>
                      <Col md={7} />

                      <Col md={5}>
                        <Separator size="tiny" />

                        <SectionSubTitle>{feature4?.title}</SectionSubTitle>
                        <SectionSubText
                          dangerouslySetInnerHTML={{
                            __html: feature4?.text ?? '',
                          }}
                        />
                      </Col>
                    </Row>
                  </ResponsiveContainer>
                </div>

                <div className="item item5">
                  <ResponsiveContainer className="container">
                    <Row>
                      <Col md={5}>
                        <Separator size="tiny" />

                        <SectionSubTitle>{feature5?.title}</SectionSubTitle>
                        <SectionSubText>{feature5?.text}</SectionSubText>
                      </Col>
                    </Row>
                  </ResponsiveContainer>

                  <div style={{ flex: 1 }} />

                  <img
                    src="/img/selected-data.svg"
                    width="632"
                    height="365"
                    className="image"
                    alt="selected data"
                  />
                </div>
              </>
            );
          })}
        </BlackBackgroundSection>

        <ResponsiveContainer
          style={{
            marginTop: '15%',
            marginBottom: '-5%',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Separator />

          <Row>
            <Col md={5} style={{ zIndex: 2 }}>
              <SectionTitle>{page?.brands?.title}</SectionTitle>
            </Col>

            <Col md={2} />

            <Col md={5} style={{ zIndex: 2 }}>
              <SectionText>{page?.brands?.text}</SectionText>
            </Col>

            <Col xs={12}>
              <DesktopOnly style={{ margin: '10% auto 10% auto' }}>
                <img
                  src="/img/brands.svg"
                  width="960"
                  height="342"
                  alt="brands"
                  style={{ width: '100%' }}
                />
              </DesktopOnly>

              <MobileOnly
                style={{ margin: '10% auto 10% auto', width: '100%' }}
              >
                <img
                  src="/img/brands-mobile.svg"
                  width="312"
                  height="523"
                  alt="brands mobile"
                  style={{ width: '100%' }}
                />
              </MobileOnly>
            </Col>
          </Row>
        </ResponsiveContainer>
      </WidthDelimiter>

      <Footer
        links={footer?.links}
        title={page?.footer?.title}
        text={page?.footer?.text}
        button={page?.footer?.button?.title}
        onButtonClick={() => Router.push(page?.footer?.button?.href ?? '')}
      />
    </>
  );
};

export async function getServerSideProps() {
  try {
    const { data } = await ContentStackClient().query<IPageProps<Developers>>({
      query: PAGE_QUERY,
    });

    return { props: data };
  } catch (e) {
    return { props: {} };
  }
}

const PAGE_QUERY = gql`
  query developersQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}

    page: developers(uid: "bltabf9d09de58b6638") {
      title
      description
      hero {
        title
        text
        button {
          title
          href
        }
      }
      uses {
        label
        title
        cases {
          title
          text
        }
      }
      api {
        title
        text
      }
      features {
        title
        text
      }
      brands {
        title
        text
      }
      footer {
        title
        text
        button {
          title
          href
        }
      }
    }
  }
`;

const DesktopOnly = styled.div`
  display: none;

  ${mediaScreen('md')} {
    display: block;
  }
`;

const MobileOnly = styled.div`
  display: none;

  ${mediaScreen('xs', 'md')} {
    display: block;
  }
`;

const WidthDelimiter = styled.div`
  max-width: 1600px;
  position: relative;
  margin: 0 auto;
  overflow: hidden;

  img {
    pointer-events: none;
    user-select: none;
  }
`;

const Hero = styled.div`
  width: 100%;
  padding-top: 160px;
  margin-bottom: 100px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 67px;
  font-weight: bold;
  line-height: 1.19;
  margin-top: 0;

  strong {
    color: ${(props) => props.theme.colors.teal};
  }

  ${mediaScreen('xs', 'sm')} {
    font-size: 35px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 50px;
  }
`;

const HeroText = styled.p`
  color: white;
  font-size: 22px;
  line-height: 1.5;
  margin-top: 50px;
  max-width: 85%;
  font-weight: 400;
  margin-bottom: 44px;

  ${mediaScreen('xs', 'sm')} {
    font-size: 17px;
    max-width: 100%;
    margin-top: 24px;
    margin-bottom: 32px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 19px;
    margin-top: 24px;
    margin-bottom: 32px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 50px;
  line-height: 1.2;
  color: white;
  font-weight: bold;
  margin-top: 0;

  ${mediaScreen('xs', 'sm')} {
    font-size: 29px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 40px;
  }
`;

const SectionSubTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 38px;
  line-height: 1.16;
  color: white;
  font-weight: bold;

  ${mediaScreen('xs', 'sm')} {
    font-size: 24px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 30px;
  }
`;

const SectionText = styled.p`
  color: white;
  font-size: 22px;
  line-height: 30px;

  ${mediaScreen('xs', 'sm')} {
    font-size: 17px;
    line-height: 24px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 19px;
    line-height: 25px;
  }
`;

const SectionSubText = styled.p`
  color: white;
  font-size: 16px;
  line-height: 1.5;

  ${mediaScreen('xs', 'sm')} {
    font-size: 14px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 16px;
  }
`;

const WhiteBackgroundSection = styled(WidthDelimiter)`
  background-image: linear-gradient(180deg, white, #efedfb);
  overflow: hidden;
  z-index: 1;

  ${mediaScreen('md')} {
    padding-bottom: 8%;
    margin-bottom: 50px;
  }

  z-index: 3;
  position: relative;
  display: flex;
  padding-top: 100px;

  .container {
    width: 100%;
  }

  label {
    font-size: 12px;
    text-transform: uppercase;
    line-height: 1.33;
    color: ${({ theme }) => theme.colors.eb1};
    font-weight: bold;
    margin-bottom: 32px;
    display: block;
  }

  ${SectionTitle}, ${SectionSubText} {
    color: ${(props) => props.theme.colors.slate1};
  }

  ${SectionSubTitle} {
    color: ${(props) => props.theme.colors.eb1};
  }

  .item {
    margin-top: 60px;

    > div:first-of-type {
      width: 100%;
      margin-top: 48px !important;
    }
  }

  .mobile-only {
    display: none;
    visibility: hidden;
  }

  ${mediaScreen('xs', 'md')} {
    padding: 50px 0;
    margin-bottom: 0px;
    margin-top: 100px;
    background: rgb(239, 237, 251);

    > div:first-of-type {
      position: relative;
      top: 0;
    }

    .background {
      display: none;
    }

    .desktop-only {
      display: none;
      visibility: hidden;
    }

    .mobile-only {
      display: block;
      visibility: visible;
    }

    .item {
      margin-top: 100px;

      &:first-of-type {
        margin-top: 50px;
      }
    }
  }
`;

const BlackBackgroundSection = styled(WidthDelimiter)`
  margin-top: 10%;

  .item {
    display: flex;
    justify-content: center;
  }

  .container {
    position: absolute;
  }

  .item0 {
    margin-bottom: 10%;

    .container {
      position: relative;
    }

    .code-snippets {
      margin-top: 80px;
    }
  }

  .item1 {
    .image {
      width: 60%;
    }
  }

  .item2 {
    margin-top: 15%;
  }

  .item3 {
    margin-top: 15%;

    .image {
      width: 95%;
      margin-right: -40%;
    }
  }

  .item4 {
    margin-top: -10%;

    .container {
      padding-top: 15%;
    }

    .image {
      width: 45%;
    }
  }

  .item5 {
    margin-top: 0%;
    align-items: center;

    .image {
      width: 67%;
      margin-right: -10%;
    }
  }

  ${mediaScreen('xs', 'md')} {
    .item1 {
      flex-direction: column;

      .container {
        position: relative;
      }

      .image {
        width: 100%;
        max-width: 100%;
        margin-top: 45px;
      }
    }

    .item2 {
      margin-top: 120px;

      > div > div {
        flex-direction: column-reverse;

        .image {
          margin-top: 60px;
        }
      }
    }

    .item3 {
      margin-top: 100px;
      flex-direction: column;

      .container {
        position: relative;
      }

      .image {
        max-width: 230%;
        width: 230%;
        margin-left: -30%;
        margin-top: 60px;
      }
    }

    .item4 {
      flex-direction: column-reverse;

      .container {
        position: relative;
      }

      .image {
        width: 90%;
        margin: 0 auto;
        margin-top: 60px;
      }
    }

    .item5 {
      margin-top: 0px;
      margin-bottom: 30px;
      flex-direction: column;

      .container {
        position: relative;
      }

      .image {
        width: 170%;
        margin-top: 0px;
      }
    }
  }
`;

export default Page;
